<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
      <input-with-error-message v-if="isRenameMode"
                                id="index-editor-name-input"
                                :value.sync="botName"
                                :is-loading="isLoading"
                                @escape="$emit('set-rename-mode', false)"
                                @submit="$emit('rename',{ ...bot, name: $event })"
                                class="h-full mt-5 w-2/3"
      />
      <div v-else class="mt-8 inline-block pr-14 relative">
        <h3 class="font-semibold">
          {{ bot.name }}
        </h3>
        <side-list-menu v-if="!isRenameMode"
                        :items="menuItems"
                        class="absolute top-0 right-0 -mt-1"
                        @rename="$emit('set-rename-mode', true)"
                        @delete="$emit('request-delete', bot)"
        />
      </div>
    </div>

    <div class="h-full flex flex-col justify-between">
      <div class="mt-8 px-8 flex flex-col gap-6 text-gray-dark">
        <div>
          <label class="font-bold">Type</label>
          <div class="mt-1 flex items-center gap-2">
            <span>{{ formatBotType(bot.type) }}</span>
            <vs-icon :icon="getIcon(bot.type)"/>
          </div>
        </div>

        <div>
          <label class="font-bold">NLP Engine</label>
          <div v-if="isNlpEngineDropdownShown" class="flex flex-col">
            <dropdown-button name="selectNlpModelType"
                             primary-button-variant="secondary"
                             primary-button-classes="w-full h-10"
                             dropdown-position="below-left"
                             class="mt-1"
                             arrow-color="gray"
                             :value="bot.nlp_model_type"
                             :options="nlpModelTypeOptions"
                             :close-on-select="true"
                             @select-button="handleSelectNlpModelType"
            />
          </div>
          <div v-else class="mt-1 flex items-center gap-2">
            <span>GPT</span>
            <vs-icon icon="bolt"/>
          </div>
        </div>

        <div>
          <label class="font-bold">Language</label>
          <div class="mt-1 flex items-center gap-2">
            <span>{{ getBotLanguage(bot.language) }}</span>
            <vs-icon icon="language"/>
          </div>
        </div>
      </div>

      <div v-if="isNlpEngineDropdownShown" class="mb-10 px-8">
        <busy-button id="saveButton"
                     class="w-1/2"
                     :variant="saveButtonVariant"
                     :is-loading="isSavingBot"
                     :is-disabled="isSavingBot"
                     @click="saveBot"
        >
          {{ saveButtonText }}
        </busy-button>
      </div>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SideListMenu from '@/components/common/SideListMenu'
import { BOT_LANGUAGES } from '@/utils/bot.constants'
import { MENU_ITEMS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import dropdownButtonsUtils from '@/utils/dropdown-button.utils'
import DropdownButton from '@/components/common/DropdownButton.vue'
import botService from '@/services/amio/bot.service'
import toaster from '@/utils/toaster'
import head from 'ramda/src/head'
import previewModule from '@/store/modules/preview.module'
import { NLP_MODEL_TYPES } from '@/utils/nlp.constants'

export default {
  name: 'SettingsBotSummary',

  components: {
    InputWithErrorMessage,
    SideListMenu,
    DropdownButton,
    BusyButton
  },

  props: {
    bot: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isRenameMode: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    bot: {
      immediate: true,
      handler(bot) {
        this.botName = bot.name
        this.nlpModelType = bot.nlp_model_type
      }
    }
  },

  data() {
    return {
      botName: '',
      nlpModelType: NLP_MODEL_TYPES.GPT,
      isSavingBot: false,
      saveButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      saveButtonText: SAVE_BUTTON_LABELS.DEFAULT
    }
  },

  computed: {
    error: previewModule.error,

    isNlpEngineDropdownShown() {
      return (this.showUnreleased || this.bot.nlp_model_type !== NLP_MODEL_TYPES.GPT) && !['111', '222'].includes(this.bot.id)
    },
    formattedBotType() {
      return this.bot.type[0].toUpperCase() + this.bot.type.slice(1)
    },
    menuItems() {
      return ['111', '222'].includes(this.bot.id) ? [MENU_ITEMS.rename] : [MENU_ITEMS.rename, MENU_ITEMS.delete]
    },
    nlpModelTypeOptions() {
      return dropdownButtonsUtils.getNlpModelTypeOptions()
    }
  },

  methods: {
    formatBotType(botType) {
      return botType[0].toUpperCase() + botType.slice(1)
    },
    getIcon(type) {
      return type === 'chat' ? 'message' : 'email_outline'
    },
    getBotLanguage(languageValue) {
      return BOT_LANGUAGES.find(language => language.value === languageValue)?.text
    },

    handleSelectNlpModelType(index) {
      this.nlpModelType = this.nlpModelTypeOptions[index].value
    },

    async saveBot() {
      this.error = null
      this.isSavingBot = true
      try {
        if (this.bot.nlp_model_type !== this.nlpModelType) {
          const updatedBot = await botService.update(this.bot.id, {
            nlp_model_type: this.nlpModelType
          })
          this.$emit('update-bot', {
            channels: this.bot.channels,
            ...updatedBot
          })
        }
      } catch (err) {
        const data = err.response?.data
        const errorMessage = head(data?.details.errors)?.message || err.message
        this.error = errorMessage
        toaster.error(errorMessage)
      } finally {
        this.startButtonStateTimeout()
        this.isSavingBot = false
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.saveButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      this.saveButtonText = SAVE_BUTTON_LABELS[buttonState]
    }
  }
}
</script>
